import React, { useState } from "react";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";

const INQUIRY_OPTIONS = [
  { value: "hire_talent", label: "Hire Talent" },
  { value: "startup_hr_setup", label: "Startup HR Setup" },
  { value: "hr_retainer", label: "Monthly HR Retainer" },
  { value: "bulk_hiring", label: "Bulk Hiring" },
  { value: "cv_building", label: "CV Building" },
  { value: "interview_prep", label: "Interview Preparation" },
  { value: "career_guidance", label: "Career Guidance" },
  { value: "other", label: "Something else" },
];

export default function ContactSection() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    inquiry_type: "hire_talent",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.company) delete payload.company;
      await api.post("/contact/submit", payload);
      toast.success("Thanks! We’ll get back to you within one business day.");
      setForm({
        name: "",
        email: "",
        phone: "",
        company: "",
        inquiry_type: "hire_talent",
        message: "",
      });
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Could not send your message. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 md:py-28" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-5">
          <span className="uppercase text-xs tracking-[0.22em] font-bold text-brand-primary">
            Contact Us
          </span>
          <h2 className="mt-3 font-heading font-medium text-3xl sm:text-4xl lg:text-5xl tracking-tight">
            Let’s build something good — together.
          </h2>
          <p className="mt-4 text-brand-muted leading-relaxed">
            Tell us a little about what you need. Whether you’re hiring, setting up HR, or
            looking for career support, we usually reply within one business day.
          </p>

          <div className="mt-8 space-y-4">
            <a
              href="mailto:hiring.careerconnect@gmail.com"
              className="flex items-center gap-4 p-4 rounded-2xl bg-white border border-brand-border hover:shadow-md transition"
              data-testid="contact-email"
            >
              <div className="w-10 h-10 rounded-xl bg-brand-soft grid place-items-center text-brand-primary">
                <Mail size={18} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-brand-muted">Email</div>
                <div className="font-medium">hiring.careerconnect@gmail.com</div>
              </div>
            </a>
            <a
              href="tel:+917774805226"
              className="flex items-center gap-4 p-4 rounded-2xl bg-white border border-brand-border hover:shadow-md transition"
              data-testid="contact-phone"
            >
              <div className="w-10 h-10 rounded-xl bg-brand-soft grid place-items-center text-brand-primary">
                <Phone size={18} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-brand-muted">Phone</div>
                <div className="font-medium">+91 77748 05226</div>
              </div>
            </a>
            <div className="flex items-center gap-4 p-4 rounded-2xl bg-white border border-brand-border">
              <div className="w-10 h-10 rounded-xl bg-brand-soft grid place-items-center text-brand-primary">
                <MapPin size={18} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-brand-muted">Based in</div>
                <div className="font-medium">Parvati, Pune – 411009</div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form
            onSubmit={handleSubmit}
            className="rounded-3xl bg-white border border-brand-border p-8 lg:p-10 shadow-sm"
            data-testid="contact-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="name" className="text-sm text-brand-ink">
                  Your name *
                </Label>
                <Input
                  id="name"
                  required
                  value={form.name}
                  onChange={handleChange("name")}
                  className="mt-2 h-11 rounded-xl border-brand-border bg-white"
                  placeholder="Jane Doe"
                  data-testid="contact-input-name"
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-sm text-brand-ink">
                  Email *
                </Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={handleChange("email")}
                  className="mt-2 h-11 rounded-xl border-brand-border bg-white"
                  placeholder="jane@example.com"
                  data-testid="contact-input-email"
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-sm text-brand-ink">
                  Phone *
                </Label>
                <Input
                  id="phone"
                  required
                  value={form.phone}
                  onChange={handleChange("phone")}
                  className="mt-2 h-11 rounded-xl border-brand-border bg-white"
                  placeholder="+91 ..."
                  data-testid="contact-input-phone"
                />
              </div>
              <div>
                <Label htmlFor="company" className="text-sm text-brand-ink">
                  Company (optional)
                </Label>
                <Input
                  id="company"
                  value={form.company}
                  onChange={handleChange("company")}
                  className="mt-2 h-11 rounded-xl border-brand-border bg-white"
                  placeholder="Acme Pvt Ltd"
                  data-testid="contact-input-company"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-sm text-brand-ink">What can we help with? *</Label>
                <Select
                  value={form.inquiry_type}
                  onValueChange={(v) => setForm((f) => ({ ...f, inquiry_type: v }))}
                >
                  <SelectTrigger
                    className="mt-2 h-11 rounded-xl border-brand-border bg-white"
                    data-testid="contact-select-inquiry"
                  >
                    <SelectValue placeholder="Choose an option" />
                  </SelectTrigger>
                  <SelectContent>
                    {INQUIRY_OPTIONS.map((o) => (
                      <SelectItem
                        key={o.value}
                        value={o.value}
                        data-testid={`contact-inquiry-option-${o.value}`}
                      >
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="message" className="text-sm text-brand-ink">
                  Tell us more *
                </Label>
                <Textarea
                  id="message"
                  required
                  value={form.message}
                  onChange={handleChange("message")}
                  className="mt-2 rounded-xl border-brand-border bg-white min-h-[140px]"
                  placeholder="A few lines about the role, your team, or what you’re looking for…"
                  data-testid="contact-input-message"
                />
              </div>
            </div>

            <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
              <p className="text-xs text-brand-muted">
                By submitting, you agree to be contacted by Career Connect.
              </p>
              <Button
                type="submit"
                disabled={submitting}
                className="rounded-full h-12 px-6 bg-brand-primary text-white hover:bg-brand-primaryHover disabled:opacity-70"
                data-testid="contact-form-submit"
              >
                {submitting ? "Sending…" : (
                  <span className="inline-flex items-center gap-2">
                    Send Message <Send size={16} />
                  </span>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
